import {Link} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {Badge, Empty, Pagination} from "antd";
import Search from "../../../components/common/Search";
import RestService from "../../../services/restService";
import Pusher from "pusher-js";
import {useSelector} from "react-redux";
import PaginationComponent from "../../../components/common/Pagination";
import AddNewAgent from "../../../components/common/AddNewAgent";
import UserIcon from "../../../components/icons/UserIcon";

const defaultImg =
  "https://www.kindpng.com/picc/m/252-2524695_dummy-profile-image-jpg-hd-png-download.png";

// Agent Avatar component with error handling
const AgentAvatar = ({ src, className }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  // Reset error state when src changes
  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [src]);

  if (!src || imageError) {
    return (
      <UserIcon 
        className="w-16 h-16" 
        bgColor="bg-blue-50" 
        iconColor="text-blue-500" 
      />
    );
  }

  return (
    <>
      {!imageLoaded && (
        <UserIcon 
          className="w-16 h-16" 
          bgColor="bg-blue-50" 
          iconColor="text-blue-500" 
        />
      )}
      <img
        src={src}
        alt=""
        className={`${className} ${!imageLoaded ? 'hidden' : ''}`}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
    </>
  );
};

const Agents = () => {
  const agent = useSelector((state) => state.auth.user);
  const [agentList, setAgentList] = useState([]);
  const [pageTo, setPageTo] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [from, setFrom] = useState(0);
  const [showAddNewAgent, setShowAddNewAgent] = useState(false);

  const setTopUpCallback = (data) => {
    let user = {...agent};
    user.balance_available = data.message?.model.balance_available;
    user.actual_balance = data.message?.model.actual_balance;
    let indexOf = -1;
    agentList.forEach((a, index) => {
      if (a.id === data.message?.model.id) {
        indexOf = index;
      }
    });

    if (indexOf >= 0) {
      agentList[indexOf] = user;
      setAgentList(agentList);
    }
  };

  const setDebtCallback = (data) => {
    let user = {...agent};
    user.balance_owed = data.message?.model.balance_owed;
    let indexOf = -1;
    agentList.forEach((a, index) => {
      if (a.id === data.message?.model.id) {
        indexOf = index;
      }
    });

    if (indexOf >= 0) {
      agentList[indexOf] = user;
      setAgentList([...agentList]);
    }
  };

  const fetchAgents = (page) => {
    RestService.AgentApi.getAgentsByAgency(agent?.agency?.id, page).then(
      ({data}) => {
        setAgentList(data?.data?.data);
        setPageTo(data?.data?.to);
        setTotalEntries(data?.data?.total);
        setFrom(data?.data?.from);
      }
    );
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (agentList.length > 0) {
      const pusher = new Pusher(
          import.meta.env.VITE_API_ENVIRONMENT === "dev"
              ? import.meta.env.VITE_PUSHER_KEY_DEVELOPMENT:
              import.meta.env.VITE_API_ENVIRONMENT === "staging" ?
                  import.meta.env.VITE_PUSHER_KEY_STAGING
                  : import.meta.env.VITE_PUSHER_KEY_PRODUCTION,
        {
          cluster: "eu",
          forceTLS: true,
        }
      );
      const channel = pusher.subscribe(
        `funding-agency-${agent?.agency?.id}-agent-${agent?.id}`
      );
      channel.bind("agent-debt-reduction", setDebtCallback);

      channel.bind("agent-evalue-topup", setTopUpCallback);
    }
  }, [agentList]);

  const searchAgentByInput = async (input) => {
    const response = await RestService.AgentApi.getAgentBySearchTerm(input);
    const agents = await response?.data?.data?.data;
    setAgentList(agents);
  };

  const pageChange = (page) => {
    setCurrentPage(page);
    fetchAgents(page);
  };

  return (
    <>
      <div className="grid  mt-[5rem] grid-cols-12 gap-6 modal-active">
        <div className="intro-y col-span-12 flex w-full justify-between flex-wrap md:flex-row-reverse sm:flex-nowrap items-center mt-2">
          <button
            onClick={() => setShowAddNewAgent(true)}
            className="btn btn-primary-2 gap-1 items-center shadow-md mr-2"
          >
            <span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.98389 1.24805C6.24188 1.24805 6.45103 1.45719 6.45103 1.71519V5.45229H10.1881C10.4461 5.45229 10.6553 5.66144 10.6553 5.91943C10.6553 6.17743 10.4461 6.38657 10.1881 6.38657H6.45103V10.1237C6.45103 10.3817 6.24188 10.5908 5.98389 10.5908C5.72589 10.5908 5.51675 10.3817 5.51675 10.1237V6.38657H1.77964C1.52165 6.38657 1.3125 6.17743 1.3125 5.91943C1.3125 5.66144 1.52165 5.45229 1.77964 5.45229H5.51675V1.71519C5.51675 1.45719 5.72589 1.24805 5.98389 1.24805Z"
                  fill="black"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>Add Agent</span>
          </button>

          <div className="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
            <div className="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
              <Search
                placeholder={"Search Agent"}
                onChange={(e) => searchAgentByInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        {agentList && agentList.length <= 0 && (
          <div className="font-bold justify-center h-[55vh] flex items-center  col-span-12">
            <Empty />
          </div>
        )}
        {agentList.map((agent, index) => {
          const label = agent.agent_role?.name;
          const fullImage = agent?.identifications?.find((x) =>
            x?.identification_category?.name?.toLowerCase()?.includes("full image")
          )?.resource_path ?? defaultImg;
          
          return (
            <div key={agent.id} className="intro-y col-span-12 md:col-span-6 lg:col-span-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] p-6 flex flex-col gap-4 relative overflow-hidden">
                {/* Available Balance Badge */}
                <div className="absolute top-0 right-0 bg-green-100 text-green-700 px-4 py-2 rounded-bl-xl text-sm font-medium">
                  {agent.agency?.country?.currency_symbol}{agent.balance_available}
                </div>
                
                {/* Header with Avatar and Info */}
                <div className="flex items-center gap-4 pt-2">
                  <div className="relative">
                    <AgentAvatar 
                      src={fullImage}
                      className="w-16 h-16 object-cover rounded-full border-2 border-gray-100"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <Link
                      to={`/agent/${agent.id}`}
                      className="text-gray-900 font-semibold text-lg hover:text-blue-600 transition-colors block"
                    >
                      {agent.firstname} {agent.lastname}
                    </Link>
                    <p className="text-gray-500 text-sm font-medium">
                      {agent.agency?.name}
                    </p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 bg-blue-100 text-blue-700">
                      {label}
                    </span>
                  </div>
                </div>

                {/* Balance Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Actual Balance</span>
                    <span className="text-gray-900 font-semibold text-lg">
                      {agent.agency?.country?.currency_symbol}{agent.actual_balance}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Link
                    to={`/statement/agent/${agent.id}`}
                    className="bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg text-center transition-colors duration-200 text-sm"
                  >
                    View Statement
                  </Link>
                  <div className="flex gap-2">
                    <Link
                      to={`/commissions/agent/${agent.id}`}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-2.5 px-3 rounded-lg text-center transition-colors duration-200 text-sm"
                    >
                      Commissions
                    </Link>
                    <Link
                      to={`/agent/${agent.id}`}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-3 rounded-lg text-center transition-colors duration-200 text-sm"
                    >
                      Profile
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col mb-[10vh] gap-4 justify-between md:items-center md:flex-row mt-8">
        <div className="page text-[#999999] text-xs">
          Showing {from} to {pageTo} of {totalEntries} entries
        </div>

        <div className="mt-3  flex justify-end float-right">
          <PaginationComponent
            current={currentPage}
            defaultCurrent={currentPage}
            showSizeChanger={false}
            onChange={pageChange}
            total={totalEntries}
          />
        </div>
      </div>

      {showAddNewAgent && (
        <AddNewAgent
          show={showAddNewAgent}
          setShow={setShowAddNewAgent}
          refetchData={fetchAgents}
        />
      )}
    </>
  );
};
export default Agents;
